import { createContext, useEffect, useState } from 'react';
import axios from 'axios';
import api from '../utils/api';
import { clearQueryCache } from '../utils/queryCache';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { LoginType } from '../pages/Login';
import { AxiosResponse } from 'axios';
import CryptoJS from 'crypto-js';
import { useLoading } from './LoadingContext';
import { useError } from './ErrorContext';

interface Authorization {
  token?: string;
  isAuth?: boolean;
  isAdmin?: boolean;
  isFamily?: boolean;
  isTeacher?: boolean;
}

interface AuthContextType {
  auth: Authorization;
  login: (email: string, password: string, category: LoginType) => Promise<void>;
  loginWithGoogle: (googleToken: string, category: LoginType) => Promise<void>;
  logout: () => void;
  register: (request: any, category: 'family' | 'teacher' | 'admin') => Promise<AxiosResponse<any, any>>;
}

const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY!;

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: any) => {
  const [auth, setAuth] = useState<Authorization>({});
  const [expirationToken, setExpirationToken] = useState<number | null>(null);
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const { setError } = useError();

  useEffect(() => {
    if (!expirationToken) return;
    if (expirationToken < Date.now()) {
      createNewToken();
    } else {
      const timeoutId = setTimeout(() => {
        createNewToken();
      }, expirationToken - Date.now());

      return () => clearTimeout(timeoutId);
    }
  }, [expirationToken]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!expirationToken) return;
      if (expirationToken < Date.now()) {
        createNewToken();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [expirationToken]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const encryptedRoles = localStorage.getItem('roles');
    let roles = null;

    try {
      if (encryptedRoles) {
        roles = JSON.parse(CryptoJS.AES.decrypt(encryptedRoles, encryptionKey).toString(CryptoJS.enc.Utf8));
      }

      if (token) {
        const decodedToken: any = jwtDecode(token);
        if (decodedToken.exp && decodedToken.exp * 1000 < Date.now()) {
          console.log('Token expired, attempting refresh...');
          createNewToken();
        } else {
          setAuth({ token, isAuth: true, ...roles });
        }
      } else {
        // Nessun token in localStorage = nessuna sessione precedente
        setAuth({ isAuth: false });
      }
    } catch (error) {
      console.error('Error processing authentication data:', error);
      logout();
    }
  }, []);

  useEffect(() => {
    if (auth.isAuth === undefined) return;
    if (auth.isAuth) {
      if (auth.token) {
        verifyToken(auth.token);
      } else {
        createNewToken();
      }
    } else {
      localStorage.removeItem('authToken');
    }
  }, [auth]);

  const verifyToken = (token: string) => {
    const decodedJWT: any = jwtDecode(token);
    if (decodedJWT && decodedJWT.exp) {
      setExpirationToken(decodedJWT.exp * 1000);
    }
  };

  const storeAuthData = (token: string, roles: object) => {
    const encryptedRoles = CryptoJS.AES.encrypt(JSON.stringify(roles), encryptionKey).toString();
    localStorage.setItem('authToken', token);
    localStorage.setItem('roles', encryptedRoles);
    setAuth({ token, isAuth: true, ...roles });
  };

  const login = async (email: string, password: string, category: LoginType) => {
    setLoading(true);
    try {
      const response = await api.post('/auth/signin', { email, password, category });
      if (response.status === 200) {
        const { token } = response.data;
        const roles = {
          isAdmin: category === 'admin',
          isFamily: category === 'family',
          isTeacher: category === 'teacher',
        };
        storeAuthData(token, roles);
        if (category === 'admin') {
          setLoading(false);
          const urlParams = new URLSearchParams(window.location.search);
          const redirect = urlParams.get('redirect');
          if (redirect && redirect.startsWith('/admin') && redirect !== '/admin/login') {
            navigate(redirect);
          } else {
            navigate('/admin');
          }
          return;
        }
        // Non disattiviamo il loading qui - verrà disattivato dalle home pages dopo aver caricato le lezioni
      }
    } catch (error: any) {
      setLoading(false);
      if (error.response && error.response.status === 401) {
        throw new Error("Password o email errata");
      } else {
        throw new Error(error.response?.data?.message || "Si è verificato un errore, riprova più tardi");
      }
    }
  };

  const loginWithGoogle = async (googleToken: string, category: LoginType) => {
    setLoading(true);
    try {
      const response = await api.post('/auth/signin', { google_token: googleToken, category });
      if (response.status === 200) {
        const { token, terms_and_conditions } = response.data;
        if (terms_and_conditions === 0) {
          sessionStorage.setItem('googleNewUser', 'true');
        }
        const roles = {
          isAdmin: category === 'admin',
          isFamily: category === 'family',
          isTeacher: category === 'teacher',
        };
        storeAuthData(token, roles);
        if (category === 'admin') {
          setLoading(false);
          const urlParams = new URLSearchParams(window.location.search);
          const redirect = urlParams.get('redirect');
          if (redirect && redirect.startsWith('/admin') && redirect !== '/admin/login') {
            navigate(redirect);
          } else {
            navigate('/admin');
          }
          return;
        }
        // Non disattiviamo il loading qui - verrà disattivato dalle home pages dopo aver caricato le lezioni
      }
    } catch (error: any) {
      setLoading(false);
      if (error.response && error.response.status === 401) {
        throw new Error("Accesso non riuscito. Riprova o effettua l'accesso con email e password.");
      } else {
        throw new Error(error.response?.data?.message || "Si è verificato un errore con Google Sign-In");
      }
    }
  };

  const logout = () => {
    api.delete('/auth/refresh-token').catch(() => {});
    localStorage.removeItem('authToken');
    localStorage.removeItem('roles');
    sessionStorage.removeItem('deletedByTeacherModalShown');
    // I dati delle pagine tenuti in memoria non devono restare a chi entra dopo
    clearQueryCache();
    setAuth({ token: '', isAuth: false });
    navigate(process.env.REACT_APP_MAINTENANCE_MODE === 'true' ? '/' : '/login');
  };

  const register = async (request: any, category: 'family' | 'teacher' | 'admin') => {
    setLoading(true);
    return api
      .post('/auth/signup/' + category, request)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        setError(true, error.response.data.message, error.response.status);
        return error.response;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const createNewToken = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL || ''}/api/auth/refresh-token`,
        {},
        { withCredentials: true }
      );
      if (response.status === 200) {
        const { token } = response.data;
        const encryptedRoles = localStorage.getItem('roles');
        if (!encryptedRoles) {
          logout();
          return;
        }
        const roles = JSON.parse(
          CryptoJS.AES.decrypt(encryptedRoles, encryptionKey).toString(CryptoJS.enc.Utf8)
        );
        storeAuthData(token, roles);
      }
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ auth, login, loginWithGoogle, logout, register }}>
      {children}
    </AuthContext.Provider>
  );
};
